import { Dimensions, FitType } from 'types';
import { Pixels } from 'utils/measurement';
import {
  fitElement,
  isFill,
  measurementToPx,
  measurementToViewport,
  numberOrPxToPx,
} from 'utils/placement';
import { TemplateAction } from '../../action-types';
import { VideoTemplateStateContent } from '../../types';
import {
  addImage,
  deleteImage,
  recropImage,
  replaceImage,
  scaleImage,
  setInitialSlidePlacement,
  updateSlideBlurRadius,
  updateSlideBorderRadius,
  updateSlidePlacement,
} from './mutations';

export default function reducer(
  state: VideoTemplateStateContent,
  action: TemplateAction,
): VideoTemplateStateContent {
  switch (action.type) {
    case 'IMAGE_ADD': {
      const { src, original, fileName, integrationData, id } = action.payload;
      return addImage(state, src, { original, fileName, integrationData, id });
    }

    case 'IMAGE_DELETE': {
      const { id } = action.payload;
      return deleteImage(state, id);
    }

    case 'IMAGE_LAYER_DOUBLE_CLICK': {
      const { id, fitType } = action.payload;
      const { canvas, slideshow } = state;
      const slide = slideshow.data[id];

      const canvasPx = {
        height: new Pixels(canvas.height),
        width: new Pixels(canvas.width),
      };

      const placement: Dimensions<Pixels> = measurementToPx(
        slide.placement,
        canvasPx,
      );

      const newPlacement = fitElement(
        placement,
        canvasPx,
        fitType || isFill(placement, canvasPx) ? 'fit' : 'fill',
      );

      const newPlacementVp = measurementToViewport(newPlacement, canvasPx);

      return updateSlidePlacement(state, id, newPlacementVp as any);
    }

    case 'IMAGE_LAYER_WHEEL': {
      const { event, id } = action.payload;
      return scaleImage(state, id, event.deltaY * 0.001);
    }

    case 'IMAGE_PLACEMENT_CHANGE': {
      const { id, placement } = action.payload;
      const { canvas, slideshow } = state;
      const slide = slideshow?.data[id];

      const canvasPx = numberOrPxToPx({
        height: canvas.height,
        width: canvas.width,
      });

      if (typeof placement === 'string') {
        return updateSlidePlacement(
          state,
          id,
          fitElement(
            measurementToPx(slide.placement, canvasPx),
            canvasPx,
            placement as FitType,
          ),
        );
      }

      return updateSlidePlacement(state, id, placement as Dimensions<Pixels>);
    }
    case 'IMAGE_BLUR_RADIUS_CHANGE': {
      const { id, blurRadius } = action.payload;
      return updateSlideBlurRadius(state, id, blurRadius);
    }

    case 'IMAGE_BORDER_RADIUS_CHANGE': {
      const { id, borderRadius } = action.payload;
      return updateSlideBorderRadius(state, id, borderRadius);
    }

    case 'IMAGE_REPLACE': {
      const {
        src,
        original,
        fileName,
        integrationData,
        id,
        previewType,
      } = action.payload;
      return replaceImage(state, id, src, previewType, {
        original,
        fileName,
        integrationData,
      });
    }

    case 'IMAGE_CROP': {
      const { src, id, metadata } = action.payload;
      return recropImage(state, id, src, metadata);
    }

    case 'IMAGE_LOAD': {
      const { id, naturalSize } = action.payload;
      return setInitialSlidePlacement(state, id, naturalSize);
    }

    default:
      return state;
  }
}
